import { Observable, of, shareReplay, tap, catchError, throwError } from 'rxjs';
import { LruCache } from './lru-cache';

interface CacheEntry<T> {
  data$: Observable<T>;
  timestamp: number;
}

interface StorageEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Cache TTL générique pour des flux Observable, avec persistance `sessionStorage` (survit
 * à un F5 — un cache purement en mémoire est détruit avec le reste de l'app JS) et éviction
 * LRU (borné : les clés peuvent varier à l'infini, ex. un terme de recherche tapé par
 * l'utilisateur, donc un `Map` illimité grossirait sans fin sur une session longue).
 *
 * Ne dépend d'aucune API MoonShared/HTTP — `factory` peut être n'importe quel Observable,
 * ce qui rend cette classe testable directement (voir ttl-session-cache.spec.ts) sans passer
 * par les mocks MoonShared, contrairement au service qui l'utilise.
 */
export class TtlSessionCache<T> {
  private readonly memory: LruCache<string, CacheEntry<T>>;

  constructor(
    private readonly storagePrefix: string,
    maxEntries = 300,
  ) {
    this.memory = new LruCache<string, CacheEntry<T>>(maxEntries);
  }

  get(key: Record<string, unknown>, factory: () => Observable<T>, ttlMs: number): Observable<T> {
    const cacheKey = JSON.stringify(key);

    const cached = this.memory.get(cacheKey);
    if (this.isValid(cached, ttlMs)) return cached!.data$;

    const fromStorage = this.readFromStorage(cacheKey, ttlMs);
    if (fromStorage) {
      const data$ = of(fromStorage.data).pipe(shareReplay(1));
      this.memory.set(cacheKey, { data$, timestamp: fromStorage.timestamp });
      return data$;
    }

    const data$ = factory().pipe(
      tap((value) => this.writeToStorage(cacheKey, value)),
      catchError((error) => {
        this.memory.delete(cacheKey);
        return throwError(() => error);
      }),
      shareReplay(1),
    );
    this.memory.set(cacheKey, { data$, timestamp: Date.now() });
    return data$;
  }

  clear(): void {
    this.memory.clear();
    this.clearStorage();
  }

  private isValid(entry: CacheEntry<T> | undefined, ttlMs: number): boolean {
    return !!entry && Date.now() - entry.timestamp < ttlMs;
  }

  private readFromStorage(cacheKey: string, ttlMs: number): StorageEntry<T> | null {
    try {
      const raw = sessionStorage.getItem(this.storagePrefix + cacheKey);
      if (!raw) return null;
      const entry = JSON.parse(raw) as StorageEntry<T>;
      if (Date.now() - entry.timestamp >= ttlMs) {
        sessionStorage.removeItem(this.storagePrefix + cacheKey);
        return null;
      }
      return entry;
    } catch {
      return null;
    }
  }

  private writeToStorage(cacheKey: string, data: T): void {
    try {
      sessionStorage.setItem(
        this.storagePrefix + cacheKey,
        JSON.stringify({ data, timestamp: Date.now() }),
      );
    } catch (error) {
      // Quota dépassé ou storage indisponible (navigation privée) — le cache mémoire reste
      // opérationnel pour la session en cours, seule la persistance cross-reload est perdue.
      console.warn(
        `Impossible de persister le cache "${this.storagePrefix}" en sessionStorage :`,
        error,
      );
    }
  }

  private clearStorage(): void {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(this.storagePrefix)) sessionStorage.removeItem(key);
      }
    } catch {
      // ignore
    }
  }
}
